import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { COMPANIES } from 'constants/Entities';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EntityFiltersActions } from 'actions';

import { FiltersProvider } from '@Sage/ssm-web5';

import SearchCompanies from 'containers/companies/components/SearchCompanies';
import HeaderTitle from 'containers/components/layouts/HeaderTitle';
import MenuFilters from 'containers/components/EntityMenu/MenuFilters';
import { getLiteral } from 'utils/getLiteral';
import { Button } from '@Sage/hoi-poi-ui';
import NewEntityFilters from 'containers/components/NewEntityFilters';

import './accountListLayout.scss';

const mapStateToProps = (state, props) => {
    const entity = props.config.entity;
    const filterKey = props.config.filterKey || entity.entity;
    let filters = state.entityFilters[filterKey];
    const showFilters = filters ? filters.show : false;

    return {
        showFilters,
        filterKey,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        toggleFilters: bindActionCreators(EntityFiltersActions, dispatch).toggleFilters,
    };
};

const AccountListLayout = memo(({ children, config, toggleFilters, showFilters, filterKey }) => {
    const renderHeader = useMemo(() => {
        return (
            <HeaderTitle
                entity={COMPANIES}
                title="title_accounts"
                defaultTitleLabel={getLiteral('label_principal_filter_accounts_all_title_web')}
                defaultOptionLabel={getLiteral('label_principal_filter_accounts_all_option_web')}
            />
        );
    }, []);

    const handleToggleFilters = useCallback(() => {
        toggleFilters(COMPANIES, null, null, filterKey);
    }, [toggleFilters, filterKey]);

    const newConfig = useMemo(() => {
        let finalConfig = { ...config };
        finalConfig.useFiltersDrawer = false;
        return finalConfig;
    }, [config]);

    const tableClasses = useMemo(() => {
        let newTableClasses = ['campaign-accounts-list__content-table'];
        if (showFilters) newTableClasses.push('show-filters');
        return newTableClasses;
    }, [showFilters]);

    const filtersClasses = useMemo(() => {
        let newFiltersClasses = ['campaign-accounts-list__content-filters'];
        if (showFilters) newFiltersClasses.push('show-filters');
        return newFiltersClasses;
    }, [showFilters]);

    return (
        <FiltersProvider>
            <div className="campaign-accounts-list-layout">
                <div className="campaign-accounts-list__header">
                    <div className="campaign-accounts-list__header--left">
                        {renderHeader}
                        <SearchCompanies useAsSimpleSearch filterKey={filterKey} />
                        {!!newConfig.quickFilters?.length && (
                            <MenuFilters
                                entity={COMPANIES}
                                filterKey={filterKey}
                                useQuickFilters={true}
                                useQuickView={newConfig.useQuickView}
                                defaultFilters={newConfig.quickFilters}
                                defaultQuickFilters={newConfig.quickFilters}
                                dontIgnoreTheseFilters={newConfig.dontIgnoreTheseFilters}
                            />
                        )}
                    </div>
                    <div className="campaign-accounts-list__header--right">
                        <Button type="secondary" size="medium" onClick={handleToggleFilters}>
                            {getLiteral('action_filters')}
                        </Button>
                    </div>
                </div>
                <div className="campaign-accounts-list__content">
                    <div className={tableClasses.join(' ')}>{children}</div>
                    <div className={filtersClasses.join(' ')}>
                        <NewEntityFilters
                            useDrawer={newConfig.useFiltersDrawer}
                            useViewsInFilters={newConfig.useViewsInFilters}
                            useViewsInMenu={newConfig.useViewsInMenu}
                            filterKey={filterKey}
                            entity={newConfig.entity}
                            defaultFilters={newConfig.defaultFilters || []}
                            defaultQuickFilters={
                                newConfig.quickFilters || newConfig.defaultQuickFilters || []
                            }
                            hiddenFilters={newConfig.hiddenFilters || []}
                            readOnlyFilters={newConfig.readOnlyFilters || []}
                            readOnlyQuickFilters={newConfig.readOnlyQuickFilters || []}
                            filtersAddButtonSchema={newConfig.filtersAddButtonSchema}
                            dontIgnoreTheseFilters={
                                newConfig.dontIgnoreTheseFilters || ['OnlySelectedType']
                            }
                        />
                    </div>
                </div>
            </div>
        </FiltersProvider>
    );
});

AccountListLayout.displayName = 'AccountListLayout';

AccountListLayout.propTypes = {
    children: PropTypes.element,
    config: PropTypes.object,
    toggleFilters: PropTypes.func,
    showFilters: PropTypes.bool,
    filterKey: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountListLayout);
